import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

/**
 * Tracks whether the learner currently has an assessment attempt open. WHY A
 * CONTEXT The language switcher lives in AILearnPage; the attempt is started
 * deep inside ContentDisplay, several layers below UnifiedPlayerView.
 */
interface AssessmentAttemptContextValue {
    /** True while an attempt is open and not yet submitted. */
    isAttemptInProgress: boolean;
    /** Called by the assessment player when an attempt starts or finishes. */
    setAttemptInProgress: (inProgress: boolean) => void;
}

const AssessmentAttemptContext = createContext<AssessmentAttemptContextValue>({
    isAttemptInProgress: false,
    // No-op default so a component rendered outside the provider — an admin
    // preview, a storybook harness — behaves exactly as it did before.
    setAttemptInProgress: () => undefined,
});

export function AssessmentAttemptProvider({ children }: { children: ReactNode }) {
    const [isAttemptInProgress, setIsAttemptInProgress] = useState(false);

    const setAttemptInProgress = useCallback((inProgress: boolean) => {
        setIsAttemptInProgress(inProgress);
    }, []);

    const value = useMemo(
        () => ({ isAttemptInProgress, setAttemptInProgress }),
        [isAttemptInProgress, setAttemptInProgress]
    );

    return (
        <AssessmentAttemptContext.Provider value={value}>
            {children}
        </AssessmentAttemptContext.Provider>
    );
}

export function useAssessmentAttemptState(): AssessmentAttemptContextValue {
    return useContext(AssessmentAttemptContext);
}
